"use client";

import { useEffect, useRef } from "react";
import { ClientLogger } from "@/lib/clientLogger";

/**
 * Typed in-process event bus for cross-tree async I/O signals.
 * NOT for app state — use Zustand / React Context for that.
 *
 * Constraint: every payload MUST be structuredClone-safe (plain JSON,
 * no functions, class instances, DOM nodes). Keeps the door open for a
 * future BroadcastChannel multi-tab adapter without per-event migration.
 */
export interface AppEventMap {
  "quota-exceeded": void;
  // 403 from any /api endpoint — modal surfaces an "insufficient permission"
  // notice without losing route context. `detail` carries the backend's
  // problem-detail message verbatim when present.
  "forbidden-action": { detail?: string };
  // Praetor IDP exchange circuit-breaker tripped. The persistent banner stays
  // up until an `idp-restored` follows.
  "idp-unavailable": void;
  "idp-restored": void;
  "mcp-server-unavailable": Array<{ serverName: string; errorMessage: string }>;
  // threadId is optional: the SSE producer falls back to the request-scoped
  // threadId when the data frame omits it, and the streaming generator itself
  // can be invoked anonymously. Subscribers treat an absent threadId as
  // "applies to the current thread".
  "title-generated": { title: string; threadId?: string };
  "history-restored": { threadId: string };
  // Continue affordance — fired by the Continue-button click handler after the
  // POST returns 202. ThreadHistoryProvider bumps `remountKey` → /messages reload →
  // `unstable_resume:true` → runtime calls `resume()` → continuation streams.
  "continue-started": { threadId: string; messageId: string };
  // Fired the moment the SSE POST returns OK — the backend has by then committed
  // the user-message INSERT and the thread's UpdatedAt bump in one transaction.
  // Subscribers (sidebar, /chats) re-fetch the thread list so the active thread
  // surfaces at position 0 without waiting for the turn to finish.
  "user-message-sent": { threadId: string };
  // Fired at every terminal SSE event (finish, provider_stream_interrupted,
  // retryable rate-limit). Subscribers reconcile any list ordering that may have
  // shifted due to generated-document bumps during the turn.
  "turn-completed": { threadId: string };
  // Loose by design — backend metadata is opaque to consumers; no field is read today.
  "context-trimmed": { [k: string]: unknown };
  // Fired by chatModelAdapters when the SSE stream emits ErrorEvent with
  // code === "anonymization_unavailable". The dedicated dialog surfaces a Retry /
  // Disable-anonymization affordance; threadId lets the dialog scope its actions.
  "anonymization-unavailable": { threadId: string };
  // Fired by chatModelAdapters when the SSE stream emits
  // data-praetor-revision-saved (main agent persisted a new revision of a
  // Praetor case document via save_revision). The PraetorRevisionToastBridge listens
  // and shows a transient toast; there is no in-app artefact to render since the
  // revision lives in PraetorIS, not as a thread-scoped generated document.
  "praetor-revision-saved": {
    praetorDocumentId: string;
    title?: string;
    threadId?: string;
  };
  "approval-success": {
    action?: string;
    documentName?: string;
    threadId?: string;
  };
  // Emitted by the global unhandledrejection handler when assistant-ui's
  // message-repository throws "Parent message not found" during history import.
  // ThreadHistoryProvider listens and forces a fresh load (self-heal).
  "thread-import-corrupted": { pathname: string };
}

type Handler<K extends keyof AppEventMap> = (payload: AppEventMap[K]) => void;

// Rest-tuple variant lets `void` keys be emitted with no second argument:
//   appEvents.emit("quota-exceeded")
//   appEvents.emit("title-generated", { title, threadId })
type EmitArgs<K extends keyof AppEventMap> =
  AppEventMap[K] extends void ? [] : [payload: AppEventMap[K]];

const handlers: Map<keyof AppEventMap, Set<Handler<keyof AppEventMap>>> = new Map();

const recentEvents: Array<{ name: keyof AppEventMap; payload: unknown; t: number }> = [];

export const appEvents = {
  emit<K extends keyof AppEventMap>(name: K, ...rest: EmitArgs<K>): void {
    const payload = rest[0] as AppEventMap[K];

    // process.env.NODE_ENV must be the literal token (not aliased to a const)
    // so Webpack DefinePlugin can replace it and Terser can drop this entire
    // branch — including the recentEvents push — from the production bundle.
    if (process.env.NODE_ENV !== "production") {
      recentEvents.push({ name, payload, t: Date.now() });
      if (recentEvents.length > 100) recentEvents.shift();
    }

    const set = handlers.get(name);
    if (!set || set.size === 0) return;

    // Snapshot iteration: a handler that unsubscribes (or subscribes) during
    // dispatch can't perturb the in-flight loop. Matches DOM addEventListener
    // semantics where the listener list is captured at dispatch time.
    for (const h of Array.from(set)) {
      try {
        (h as Handler<K>)(payload);
      } catch (err) {
        // Match today's window.dispatchEvent semantics (browsers swallow
        // listener exceptions per-listener) so one buggy subscriber can't
        // break siblings. Surface via telemetry for diagnosis.
        ClientLogger.error(`Event handler for "${String(name)}" threw`, {
          error: err instanceof Error ? err : undefined, component: "appEvents",
        });
      }
    }
  },

  on<K extends keyof AppEventMap>(name: K, handler: Handler<K>): () => void {
    let set = handlers.get(name);
    if (!set) {
      set = new Set();
      handlers.set(name, set);
    }
    set.add(handler as Handler<keyof AppEventMap>);
    return () => {
      set!.delete(handler as Handler<keyof AppEventMap>);
    };
  },
};

/**
 * Subscribe to an app event for the lifetime of the calling component.
 *
 * Uses the latest-callback pattern: the handler ref is synced every render,
 * but the underlying subscription is only re-established when `name` changes.
 * Consumers can pass inline closures that close over render-scope state
 * (e.g. `remoteId`) without re-subscribing on every render and without
 * needing `useCallback`.
 */
export function useAppEvent<K extends keyof AppEventMap>(
  name: K,
  handler: Handler<K>,
): void {
  const ref = useRef(handler);

  useEffect(() => {
    ref.current = handler;
  });

  useEffect(() => appEvents.on(name, (p) => ref.current(p)), [name]);
}

/**
 * Dev-only: returns a snapshot of the most recent ~100 emitted events for
 * debugging. In production this returns a frozen empty array, and the entire
 * call site tree-shakes out of the bundle.
 */
function getRecentEvents(): ReadonlyArray<{
  name: keyof AppEventMap;
  payload: unknown;
  t: number;
}> {
  if (process.env.NODE_ENV === "production") return Object.freeze([]);
  return recentEvents.slice();
}

// Surface the ring buffer on `window.__praetorEventLog` in dev for direct
// DevTools-console inspection without an import. SSR-safe (typeof window
// guards the assignment) and prod-safe (process.env.NODE_ENV gates strip
// the entire block at build time).
if (
  typeof window !== "undefined" &&
  process.env.NODE_ENV !== "production"
) {
  Object.defineProperty(window, "__praetorEventLog", {
    get: () => recentEvents.slice(),
    configurable: true,
  });
}
